/**
 * Centralized API Client for DaVinci Portal
 *
 * Provides a configured axios instance with:
 * - Base URL from environment
 * - Request/response interceptors
 * - Consistent error handling
 * - Auth token placeholder (for future implementation)
 */
import axios from 'axios';

// Use VITE_API_BASE_URL if provided, otherwise default to localhost:8081 for local dev
// In Docker/production, set VITE_API_BASE_URL to empty string '' to use relative URLs (proxied by nginx)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL !== undefined
  ? import.meta.env.VITE_API_BASE_URL
  : 'http://localhost:8081';
const API_DEBUG = import.meta.env.VITE_API_DEBUG === 'true';

// Create axios instance with default config
// NOTE: Do NOT set a default Content-Type header here.
// Axios 1.x auto-detects the correct Content-Type per request:
//   - JSON objects  → application/json
//   - FormData      → multipart/form-data (with boundary)
//   - Strings       → text/plain
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // Send session cookies for dev-login auth
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Debug logging
    if (API_DEBUG) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.params || '');
    }

    // Pass through AbortSignal for React StrictMode double-fetch prevention
    // Usage: apiClient.get(url, { signal: abortController.signal })
    // The signal will be included in the axios config if provided

    // Placeholder for auth token - will be added after auth implementation
    // const token = localStorage.getItem('authToken');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }


    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (API_DEBUG) {
      console.log(`[API Response] ${response.status} ${response.config.url}`, response.data);
    }
    return response;
  },
  (error) => {
    // Normalize error response
    const normalizedError = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      status: error.response?.status || 0,
      statusText: error.response?.statusText || 'Network Error',
      errors: error.response?.data?.errors || [],
      code: error.code || 'UNKNOWN_ERROR',
      originalError: error,
    };

    // Log error in debug mode
    if (API_DEBUG) {
      console.error('[API Error]', normalizedError);
    }

    // Handle specific error codes
    switch (normalizedError.status) {
      case 401:
        // Unauthorized - could trigger logout or token refresh
        console.warn('Unauthorized access - authentication required');
        break;
      case 403:
        console.warn('Forbidden - insufficient permissions');
        break;
      case 404:
        console.warn('Resource not found');
        break;
      case 500:
        console.error('Server error');
        break;
      default:
        break;
    }

    return Promise.reject(normalizedError);
  }
);

/**
 * Helper function to handle API errors consistently
 * @param {Error} error - The error from API call
 * @returns {Object} Normalized error object
 */
export const handleApiError = (error) => {
  if (error.message && error.status !== undefined) {
    // Already normalized
    return error;
  }
  return {
    message: error.message || 'An unexpected error occurred',
    status: 0,
    errors: [],
  };
};

/**
 * Helper to check if error is a network error
 * @param {Object} error - Normalized error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return error.status === 0 || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
};

/**
 * Helper to check if error is an authentication error
 * @param {Object} error - Normalized error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  return error.status === 401 || error.status === 403;
};

export default apiClient;
